import type { Fixture, VerifiedMatchRecord } from '../lib/types';
import type { SupporterSignalSnapshot } from '../lib/truth';

const EVENT_LABEL: Record<string, string> = {
  goal: 'Goal', 'own-goal': 'Own goal', pen: 'Penalty', red: 'Red card', yellow: 'Yellow card',
  sub: 'Substitution', var: 'VAR', note: 'Match note',
};

export function MatchRibbon({ fixture, record, snapshots }: {
  fixture: Fixture;
  record: VerifiedMatchRecord | null;
  snapshots: SupporterSignalSnapshot[];
}) {
  const publicSnapshots = snapshots.filter((snapshot) => snapshot.predictions || snapshot.gauge || snapshot.discussion != null);
  return <section className="match-ribbon" aria-labelledby="match-ribbon-title">
    <header className="match-ribbon__head">
      <div><p className="match-ribbon__kicker">THE MATCH, AS IT HAPPENED</p><h2 id="match-ribbon-title">Match ribbon</h2></div>
      <span>{record ? 'Confirmed record' : 'Record pending'}</span>
    </header>
    <div className="match-ribbon__track" aria-hidden="true"><i /><i /><i /></div>
    <ol className="match-ribbon__events">
      <li><time>KO</time><span><strong>Kick-off</strong><small>{fixture.comp}</small></span></li>
      {record?.events.map((event, index) => <li key={`${event.min ?? 'u'}-${event.type}-${index}`}>
        <time>{event.min == null ? <><span aria-hidden="true">—</span><span className="visually-hidden">Time not published</span></> : `${event.minutePrecision === 'approximate' ? '≈' : ''}${event.min}′`}</time>
        <span><strong>{EVENT_LABEL[event.type] ?? 'Event'} · {event.ours == null ? 'Match' : event.ours ? 'Arsenal' : fixture.opponent}</strong><small>{event.player ?? 'Player not published'}{event.detail ? ` · ${event.detail}` : ''}</small></span>
      </li>)}
      {record ? <li><time>FT</time><span><strong>Full time</strong><small>{record.summary}</small></span></li> : null}
    </ol>
    {!record?.events.length ? <p className="match-ribbon__withheld">The score is part of the confirmed record. Event detail is not available for this match.</p> : null}
    {publicSnapshots.length ? <div className="match-ribbon__signal" aria-label="Supporter signal">
      <div className="match-ribbon__signal-head"><strong>Supporter signal</strong><span>Anonymous matchday reading</span></div>
      <div className="match-ribbon__signal-line" tabIndex={0} role="region" aria-label="Supporter signal timeline; scroll horizontally for every snapshot">
        {publicSnapshots.map((snapshot) => <span key={snapshot.capturedAt} className="match-ribbon__signal-point">
          <i />
          <b>{snapshot.minute != null ? `${snapshot.minute}′` : snapshot.phase.toUpperCase()}</b>
          <small>{snapshot.gauge ? `${snapshot.gauge.average}/10` : snapshot.discussion != null ? `${snapshot.discussion} takes` : `${Object.values(snapshot.predictions ?? {}).reduce((sum, value) => sum + Number(value), 0)} calls`}</small>
        </span>)}
      </div>
    </div> : null}
  </section>;
}
